import React, { useCallback, useState } from 'react';
import PageContainer from './PageContainer';
import { Box, Button, FormControl, FormHelperText } from '@mui/material';
import OnboardingHeader from '../onboarding/OnboardingHeader';
import { spaces } from '../../assets/styles/themes/tokens';
import useScreenSize from '../../utils/useScreenSize';
import services from '../../services';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { password } from '../../validators';
import * as yup from 'yup';
import LabelledTextField from '../LabelledTextField/LabelledTextField';
import FooterLegalNavigation from './FooterLegalNavigation';
import i18next from 'i18next';
export interface ChangeEmailPayload {
  oldEmail: string;
  password: string;
  newEmail: string;
  isLibraryUser?: boolean;
}

interface ChangeEmailProps {
  onChangeEmailSuccess: (payload: ChangeEmailPayload) => void;
}

const ChangeEmailFormValidator = yup.object<ChangeEmailPayload>({
  oldEmail: yup.string().email('Invalid email address'),
  newEmail: yup.string().email('Invalid email address').notOneOf([yup.ref('oldEmail')], 'E-Mails must not match'),
  password: password()
});

const getErrorMessage = (error: Error, t: i18next.TFunction) => {
  if (error.message.match(/Invalid username or password/)) {
    return t('Invalid username or password');
  }

  return t('Please try to refresh the page or set your password again');
};

const ChangeEmail: React.FC<ChangeEmailProps> = ({ onChangeEmailSuccess }) => {
  const { isXsScreen } = useScreenSize();
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const {
    errors,
    handleSubmit,
    register
  } = useForm<ChangeEmailPayload>({ mode: 'onBlur', validationSchema: ChangeEmailFormValidator });
  
  const onSubmit = handleSubmit(useCallback(async (payload: ChangeEmailPayload) => {
    try {
      const { oldEmail, newEmail, password } = payload;
      await services.user.changeEmail(oldEmail, newEmail, password, true);
      onChangeEmailSuccess({ ...payload, isLibraryUser: true });
    } catch (error) {
      const err = error as Error;
      setError(getErrorMessage(err, t));
    }
  }, [onChangeEmailSuccess, t]));

  return (
    <PageContainer>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: isXsScreen ? spaces.xl : spaces.xxxl,
      }}>
        <OnboardingHeader 
          title={t('Update email address')} 
          subtitle={
            t(
              'Enter your existing email address and password, and enter a new private email address ' +
              'so you can continue using your free account and saved content.'
            )
          }
        />
        <Box
          component='form'
          onSubmit={onSubmit}
          data-testid='login-form'
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <FormControl fullWidth sx={{ gap: spaces.md }}>
            <LabelledTextField
              dataTestId='old-email-input'
              name='oldEmail'
              label={t('Old E-Mail *')}
              placeholder={t('eg max.mustermann@beispiel.de')}
              inputRef={register}
              error={errors.oldEmail}
            />
            <LabelledTextField
              dataTestId='form-password-input'
              name='password'
              label={t('Password *')}
              placeholder={t('Enter your password')}
              isPasswordTextField
              inputRef={register}
              error={errors.password}
            />
            <LabelledTextField
              dataTestId='new-email-input'
              name='newEmail'
              label={t('New E-Mail *')}
              placeholder={t('eg max.mustermann@beispiel.de')}
              inputRef={register}
              error={errors.newEmail}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: spaces.md, marginTop: spaces.md }}>
              <FormHelperText error={!!error}>{error}</FormHelperText>
              <Button
                data-testid='submit-button'
                variant='contained'
                size='large'
                color='primary'
                type='submit'
                sx={{
                  width: '100%',
                }}
              >
                {t('Next')}
              </Button>
            </Box> 
          </FormControl>
        </Box>
      </Box>
      <FooterLegalNavigation isSmallScreen={isXsScreen} />
    </PageContainer>
  );
};

export default ChangeEmail;
